import type {
  AdminDeskTutorInfo,
  DeskTutorChange,
  DeletedChat,
  AdminLogEntry,
  AdminOfficialPlugin,
  KnownSystem,
  OfficialPlugin,
  AdminOverview,
  AdminPlanFilter,
  AdminPlanRow,
  AdminRoomRow,
  AdminRoomSort,
  AdminServerInfo,
  PlanConfig,
  PublicPlanCatalog,
  AdminUserDetail,
  AdminUserFilter,
  AdminUserRow,
  AdminUserSort,
  ContactList,
  ContactState,
  ContactView,
  CreateRoomInput,
  DeskCloudSummary,
  DeskFile,
  DeskFilePage,
  ExploreFacets,
  ExploreQueryInput,
  FileBulk,
  FileKind,
  FilePatch,
  FileSort,
  FileView,
  FileZip,
  ExploreTab,
  GameSessionDetail,
  GameSessionNotice,
  GameSessionPatch,
  GameSessionRatingInput,
  GameSessionSummary,
  RoomSettingsPatchInput,
  ImsMessage,
  ImsPeer,
  PlanCeilings,
  PlanId,
  PlanView,
  Privacy,
  ProfileUpdate,
  Room,
  RoomCard,
  User,
  AccountEmailView,
  EmailChangeView,
  EmailConfirmationView,
  UserProfile,
} from './shared.js';
import { serverUrl } from './serverUrl.js';
import { SYSTEMS_SEPARATOR, serverText } from './shared.js';
import { translate, useLanguage, type Language } from './i18n/index.js';

/** Os textos de erro do próprio cliente, na língua escolhida. */
const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

const TOKEN_KEY = 'rpgdesk.token';

export function loadToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY) ?? window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Guarda a sessão. Com "manter conectado" ela sobrevive ao fechar o app
 * (localStorage); sem ele, vale só enquanto a janela estiver aberta.
 */
export function saveToken(token: string | null, persist = true): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(TOKEN_KEY);
    if (token) (persist ? window.localStorage : window.sessionStorage).setItem(TOKEN_KEY, token);
  } catch {
    // armazenamento indisponível (modo privado): segue sem persistir a sessão
  }
}

export type GrammaticalGender = 'masculine' | 'feminine' | 'neutral';
export type RegisterFieldError = 'required' | 'tooShort' | 'tooLong' | 'invalid' | 'taken';

export type RegisterInput = {
  displayName: string;
  email: string;
  password: string;
  grammaticalGender: GrammaticalGender;
  wantPromoMail: boolean;
  acceptTos: true;
  /** a língua da tela: a do e-mail da confirmação (docs/spec/email.md) */
  language?: Language;
};

/**
 * O que o entrar, o cadastro e o /api/me devolvem. `tosPending`: a conta não aceitou a versão atual
 * dos termos (docs/spec/termos.md). `emailConfirmation`: a conta espera a confirmação do e-mail
 * (docs/spec/email.md); null quando ela já confirmou.
 */
export type SessionPayload = {
  user: User;
  admin: boolean;
  plan: PlanView;
  ceilings: PlanCeilings;
  plans: PublicPlanCatalog;
  tosPending: boolean;
  emailConfirmation: EmailConfirmationView | null;
};

/** Erro de um pedido ao servidor; a mensagem do servidor (em português) sai na língua escolhida. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** o código do erro do servidor (`quota`, `too-large`…), quando veio */
    readonly code?: string,
  ) {
    super(serverText(message, useLanguage.getState().language));
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  token?: string | null;
};

/** Quem cuida da sessão que o servidor recusou (a store: sai da conta). */
let onSessionRefused: (() => void) | null = null;

/** Um 401 numa chamada com sessão quer dizer que ela não vale mais (saiu noutra aba, foi suspensa). */
export function setSessionRefusedHandler(handler: (() => void) | null): void {
  onSessionRefused = handler;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(serverUrl(path), {
      method: options.method ?? 'GET',
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(options.token ? { authorization: `Bearer ${options.token}` } : {}),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiError(0, say('api.unreachable'));
  }

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    // não é JSON (a página de erro de um proxy, por exemplo): com erro, vale o status, abaixo
    if (response.ok) throw new ApiError(response.status, say('api.unexpected'));
  }

  if (!response.ok) {
    // o login com a senha errada também é 401, mas vai sem sessão
    if (response.status === 401 && options.token) onSessionRefused?.();
    const error = typeof payload === 'object' && payload !== null && 'error' in payload ? (payload as { error: { message?: string; code?: string } }).error : null;
    const message = error ? String(error.message ?? say('api.unexpected')) : say('api.status', { status: response.status });
    throw new ApiError(response.status, message, error?.code);
  }

  return payload as T;
}

/** A listagem do Desk Cloud: a visão, o filtro de tipo, a busca, a ordem e a página. */
export type FileListQuery = {
  view?: FileView;
  roomId?: string;
  folderId?: string;
  kind?: FileKind;
  images?: boolean;
  /** só os pacotes de plugin (.zip e .html) */
  plugins?: boolean;
  q?: string;
  sort?: FileSort;
  offset?: number;
};

/** O envio de um arquivo: a mesa (liga), a pasta, o tipo, as medidas, o progresso (0 a 1) e o cancelar. */
export type UploadOptions = {
  roomId?: string | null;
  folderId?: string | null;
  kind?: FileKind;
  width?: number;
  height?: number;
  durationMs?: number;
  onProgress?: (ratio: number) => void;
  signal?: AbortSignal;
};

export const api = {
  health: () => request<{ ok: boolean }>('/api/health'),

  validateRegistration: (displayName: string, email: string) =>
    request<{ ok: boolean; errors: { displayName?: RegisterFieldError; email?: RegisterFieldError } }>(
      '/api/register/validate',
      { method: 'POST', body: { displayName, email } },
    ),

  register: (input: RegisterInput) => request<SessionPayload & { token: string }>('/api/register', { method: 'POST', body: input }),

  login: (displayName: string, password: string) => request<SessionPayload & { token: string }>('/api/login', { method: 'POST', body: { displayName, password } }),

  logout: (token: string) => request<{ ok: boolean }>('/api/logout', { method: 'POST', token }),

  me: (token: string) => request<SessionPayload>('/api/me', { token }),

  /** Aceita os termos na versão que a tela mostrou (docs/spec/termos.md); outra versão volta 409. */
  acceptTos: (token: string, version: number) => request<{ ok: boolean }>('/api/me/tos', { method: 'POST', token, body: { version } }),

  /**
   * O link do e-mail da confirmação (docs/spec/email.md): vale sem a sessão. `changed`: era o link da
   * troca do e-mail, e o endereço novo passou a valer. 404: não vale mais; 410: venceu; 409: outra conta
   * pegou o endereço novo.
   */
  confirmEmail: (code: string) => request<{ ok: boolean; changed?: boolean }>('/api/email/confirm', { method: 'POST', body: { token: code } }),

  /** "Esqueci a senha": se o servidor manda o link (sem o endereço do site, não manda). */
  forgotPasswordAvailable: () => request<{ available: boolean }>('/api/password/forgot'),

  /** Pede o link da recuperação pelo nome da conta ou pelo e-mail; a resposta é sempre a mesma. */
  forgotPassword: (account: string, language: Language) => request<{ ok: boolean }>('/api/password/forgot', { method: 'POST', body: { account, language } }),

  /** De que conta é o link da recuperação, sem gastá-lo. 404: não vale mais; 410: venceu. */
  checkPasswordReset: (code: string) => request<{ displayName: string }>('/api/password/reset/check', { method: 'POST', body: { token: code } }),

  /** Troca a senha pelo link; todas as sessões da conta saem. */
  resetPassword: (code: string, password: string) =>
    request<{ ok: boolean; displayName: string }>('/api/password/reset', { method: 'POST', body: { token: code, password } }),

  /** Outro envio do link, para a conta que espera a confirmação. */
  resendConfirmation: (token: string, language: Language) =>
    request<{ emailConfirmation: EmailConfirmationView | null }>('/api/me/email/resend', { method: 'POST', token, body: { language } }),

  /** Corrige o e-mail da conta que espera a confirmação: o link vai para o novo. */
  changePendingEmail: (token: string, email: string, language: Language) =>
    request<{ emailConfirmation: EmailConfirmationView | null }>('/api/me/email', { method: 'PUT', token, body: { email, language } }),

  listRooms: (token: string) => request<{ rooms: RoomCard[] }>('/api/rooms', { token }),

  explore: (token: string, tab: ExploreTab, params: ExploreQueryInput = {}) => {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === '' || value === false) continue;
      if (Array.isArray(value)) {
        if (value.length) qs.set(key, value.join(key === 'systems' ? SYSTEMS_SEPARATOR : ','));
      } else qs.set(key, String(value));
    }
    const suffix = qs.toString();
    return request<{ rooms: RoomCard[] } & ExploreFacets>(`/api/explore/${tab}${suffix ? `?${suffix}` : ''}`, { token });
  },

  updateRoomSettings: (token: string, roomId: string, patch: RoomSettingsPatchInput) =>
    request<{ room: RoomCard }>(`/api/rooms/${roomId}`, { method: 'PATCH', token, body: patch }),

  roomPeople: (token: string, roomId: string) => request<{ auxGms: User[]; bans: User[] }>(`/api/rooms/${roomId}/people`, { token }),

  addRoomPerson: (token: string, roomId: string, list: 'aux-gms' | 'bans', account: string) =>
    request<{ user: User }>(`/api/rooms/${roomId}/${list}`, { method: 'POST', token, body: { account } }),

  removeRoomPerson: (token: string, roomId: string, list: 'aux-gms' | 'bans', userId: string) =>
    request<{ removed: boolean }>(`/api/rooms/${roomId}/${list}/${userId}`, { method: 'DELETE', token }),

  /** Link assinado do arquivo, já no endereço do servidor (src/serverUrl.ts). */
  fileUrl: (token: string, fileId: string) =>
    request<{ url: string; name: string; mime: string; size: number }>(`/api/files/${fileId}/url`, { token }).then((file) => ({ ...file, url: serverUrl(file.url) })),

  getRoom: (token: string, roomId: string) => request<{ room: RoomCard }>(`/api/rooms/${roomId}`, { token }),

  createRoom: (token: string, input: Partial<CreateRoomInput> & { name: string }) =>
    request<{ room: RoomCard }>('/api/rooms', { method: 'POST', token, body: input }),

  setFavorite: (token: string, roomId: string, favorite: boolean) =>
    request<{ room: RoomCard }>(`/api/rooms/${roomId}/favorite`, {
      method: 'POST',
      token,
      body: { favorite },
    }),

  voteRoom: (token: string, roomId: string, value: -1 | 0 | 1) =>
    request<{ room: RoomCard }>(`/api/rooms/${roomId}/vote`, {
      method: 'POST',
      token,
      body: { value },
    }),

  /* ---------------------------------------------------------------- contatos */

  searchUsers: (token: string, query: string) =>
    request<{ users: { user: User; relation: ContactState | null }[] }>(
      `/api/users/search?q=${encodeURIComponent(query)}`,
      { token },
    ),

  listContacts: (token: string) => request<{ contacts: ContactList; unread: number }>('/api/contacts', { token }),

  /** pedido de amizade pela pessoa (perfil, busca) ou pelo nome digitado ("Adicionar amigo") */
  requestContact: (token: string, target: { userId: string } | { displayName: string }, message: string | null) =>
    request<{ contact: ContactView | null }>('/api/contacts/requests', {
      method: 'POST',
      token,
      body: { ...target, message },
    }),

  contactAction: (token: string, userId: string, action: 'accept' | 'reject' | 'block' | 'unblock' | 'hide' | 'unhide') =>
    request<{ contact: ContactView | null }>(`/api/contacts/${userId}/${action}`, { method: 'POST', token }),

  /** o histórico da conversa (não marca lido) e com quem ela é (sem `peer`, servidor antigo) */
  listDirectMessages: (token: string, userId: string) =>
    request<{ messages: ImsMessage[]; unread: number; peer?: ImsPeer }>(`/api/ims/${userId}/messages`, { token }),

  /** "Tirar da lista" uma conversa com quem não é amigo */
  dismissDirectConversation: (token: string, userId: string) => request<{ unread: number }>(`/api/ims/${userId}/dismiss`, { method: 'POST', token }),

  /** de quem recebo mensagens diretas */
  getPrivacy: (token: string) => request<{ privacy: Privacy }>('/api/me/privacy', { token }),
  setPrivacy: (token: string, privacy: Privacy) => request<{ privacy: Privacy }>('/api/me/privacy', { method: 'PUT', token, body: privacy }),

  /** a conversa foi vista: marca lido o que veio da pessoa */
  markDirectMessagesRead: (token: string, userId: string) => request<{ unread: number }>(`/api/ims/${userId}/read`, { method: 'POST', token }),

  /** chama amigos para a mesa (o convite vai na conversa com cada um) */
  inviteFriends: (token: string, roomId: string, userIds: string[]) =>
    request<{ messages: ImsMessage[] }>(`/api/rooms/${roomId}/invites`, { method: 'POST', token, body: { userIds } }),

  /** `lang`: a língua da interface, para o José ler a documentação certa (docs/spec/desk-tutor.md) */
  sendDirectMessage: (token: string, userId: string, text: string, lang?: 'pt' | 'en') =>
    request<{ message: ImsMessage }>(`/api/ims/${userId}/messages`, {
      method: 'POST',
      token,
      body: { text, ...(lang ? { lang } : {}) },
    }),

  /* ---------------------------------------------------------------- perfil */

  getProfile: (token: string, userId: string) => request<{ profile: UserProfile }>(`/api/users/${userId}/profile`, { token }),


  updateProfile: (token: string, patch: ProfileUpdate) =>
    request<{ profile: UserProfile }>('/api/profile', { method: 'PATCH', token, body: patch }),

  voteUser: (token: string, userId: string, value: -1 | 0 | 1) =>
    request<{ profile: UserProfile }>(`/api/users/${userId}/vote`, { method: 'POST', token, body: { value } }),

  /* ---------------------------------------------------------------- conta */

  changePassword: (token: string, currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>('/api/password', {
      method: 'POST',
      token,
      body: { currentPassword, newPassword },
    }),

  deleteAccount: (token: string, password: string) =>
    request<{ ok: boolean }>('/api/account', { method: 'DELETE', token, body: { password } }),

  /** O e-mail da conta, a troca que espera o link e se o servidor manda links (docs/spec/email.md). */
  accountEmail: (token: string) => request<AccountEmailView>('/api/me/email', { token }),

  /** Começa a troca do e-mail, com a senha atual: o link vai para o endereço novo. */
  startEmailChange: (token: string, email: string, password: string, language: Language) =>
    request<{ change: EmailChangeView | null }>('/api/me/email/change', { method: 'POST', token, body: { email, password, language } }),

  /** Outro envio do link da troca, para o mesmo endereço novo. */
  resendEmailChange: (token: string, language: Language) =>
    request<{ change: EmailChangeView | null }>('/api/me/email/change/resend', { method: 'POST', token, body: { language } }),

  /** Desiste da troca: a conta fica com o e-mail de antes. */
  cancelEmailChange: (token: string) => request<{ ok: boolean }>('/api/me/email/change', { method: 'DELETE', token }),

  /* ---------------------------------------------------------------- Desk Cloud (docs/spec/desk-cloud.md) */

  listFiles: (token: string, query: FileListQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== '') params.set(key, String(value));
    const qs = params.toString();
    return request<DeskFilePage>(`/api/files${qs ? `?${qs}` : ''}`, { token });
  },

  fileSummary: (token: string) => request<DeskCloudSummary>('/api/files/summary', { token }),

  patchFile: (token: string, id: string, patch: FilePatch) => request<{ file: DeskFile | null }>(`/api/files/${id}`, { method: 'PATCH', token, body: patch }),

  bulkFiles: (token: string, body: FileBulk) => request<{ changed: number }>('/api/files/bulk', { method: 'POST', token, body }),

  /** Liga e desliga arquivos de mesas de uma vez; devolve as mesas em que algo mudou. */
  setFileRooms: (token: string, ids: string[], add: string[], remove: string[]) =>
    request<{ rooms: string[] }>('/api/files/rooms', { method: 'POST', token, body: { ids, add, remove } }),

  createFolder: (token: string, name: string, parentId: string | null) => request<{ id: string }>('/api/files/folders', { method: 'POST', token, body: { name, parentId } }),

  updateFolder: (token: string, id: string, patch: { name?: string; parentId?: string | null }) =>
    request<{ ok: boolean }>(`/api/files/folders/${id}`, { method: 'PATCH', token, body: patch }),

  deleteFolder: (token: string, id: string) => request<{ trashed: number }>(`/api/files/folders/${id}`, { method: 'DELETE', token }),

  /** O dono usou o arquivo ou abriu a pré-visualização (a visão Recentes). */
  fileUsed: (token: string, id: string) => request<{ ok: boolean }>(`/api/files/${id}/used`, { method: 'POST', token }),

  /** O endereço do zip (já no servidor): a ficha vale 5 minutos e uma vez só. */
  zipFiles: (token: string, body: FileZip) => request<{ url: string }>('/api/files/zip', { method: 'POST', token, body }).then(({ url }) => serverUrl(url)),

  /** A aba Sessões (docs/spec/session-clock.md): as sessões da mesa, da mais nova para a mais velha; `before` traz a página seguinte. */
  roomSessions: (token: string, roomId: string, before?: number) =>
    request<{ sessions: GameSessionSummary[]; hasMore: boolean }>(`/api/rooms/${roomId}/sessions${before ? `?before=${before}` : ''}`, { token }),

  /** O registro de uma sessão: o resumo com as notas, os passos e quem esteve. */
  roomSession: (token: string, roomId: string, number: number) => request<GameSessionDetail>(`/api/rooms/${roomId}/sessions/${number}`, { token }),

  /** O mestre muda o título e/ou as notas de uma sessão. */
  updateRoomSession: (token: string, roomId: string, number: number, patch: GameSessionPatch) =>
    request<{ session: GameSessionSummary }>(`/api/rooms/${roomId}/sessions/${number}`, { method: 'PATCH', token, body: patch }),

  /** O mestre apaga uma sessão: o registro dela e as linhas dela no chat somem para todos. */
  deleteRoomSession: (token: string, roomId: string, number: number) => request<{ ok: boolean }>(`/api/rooms/${roomId}/sessions/${number}`, { method: 'DELETE', token }),

  /** O /audit (docs/spec/chat-delete.md): as linhas apagadas do chat da mesa, com quem apagou e quando; só para os mestres. */
  deletedChat: (token: string, roomId: string) => request<DeletedChat>(`/api/rooms/${roomId}/chat/deleted`, { token }),

  /** Quem jogou a sessão dá a nota dela (docs/spec/session-clock.md, "Avaliações"); os mestres leem sem o nome. */
  rateSession: (token: string, roomId: string, number: number, rating: GameSessionRatingInput) =>
    request<{ ok: boolean }>(`/api/rooms/${roomId}/sessions/${number}/rating`, { method: 'PUT', token, body: rating }),

  unrateSession: (token: string, roomId: string, number: number) => request<{ ok: boolean }>(`/api/rooms/${roomId}/sessions/${number}/rating`, { method: 'DELETE', token }),

  /** Um mestre dá a nota de um jogador da sessão; o jogador lê, com o nome do mestre. */
  ratePlayer: (token: string, roomId: string, number: number, playerId: string, rating: GameSessionRatingInput) =>
    request<{ ok: boolean }>(`/api/rooms/${roomId}/sessions/${number}/players/${playerId}/rating`, { method: 'PUT', token, body: rating }),

  unratePlayer: (token: string, roomId: string, number: number, playerId: string) =>
    request<{ ok: boolean }>(`/api/rooms/${roomId}/sessions/${number}/players/${playerId}/rating`, { method: 'DELETE', token }),

  /** Os avisos de avaliação que ainda não chegaram; buscar apaga. */
  takeSessionNotices: (token: string) => request<{ notices: GameSessionNotice[] }>('/api/me/session-notices', { method: 'POST', token }),

  /** O painel Arquivos da mesa (os mestres veem os arquivos dos mestres; os outros, só os seus). */
  roomFiles: (token: string, roomId: string, query: { kind?: FileKind; q?: string } = {}) => {
    const params = new URLSearchParams();
    if (query.kind) params.set('kind', query.kind);
    if (query.q) params.set('q', query.q);
    const qs = params.toString();
    return request<{ files: DeskFile[]; gm: boolean }>(`/api/rooms/${roomId}/files${qs ? `?${qs}` : ''}`, { token });
  },

  async uploadThumb(token: string, id: string, image: Blob): Promise<{ thumbUrl: string }> {
    const response = await fetch(serverUrl(`/api/files/${id}/thumb`), { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': image.type }, body: image });
    const payload = (await response.json().catch(() => null)) as { thumbUrl?: string; error?: { message?: string } } | null;
    if (!response.ok || !payload?.thumbUrl) throw new ApiError(response.status, payload?.error?.message ?? say('api.status', { status: response.status }));
    return { thumbUrl: payload.thumbUrl };
  },

  /**
   * Envia o arquivo cru como corpo da requisição (sem multipart): o nome, a mesa, a pasta, o tipo e as
   * medidas vão na query, e o tipo do arquivo no cabeçalho. Por XHR, e não `fetch`, para ter o
   * progresso (docs/spec/desk-cloud.md, "Envios"); `signal` cancela.
   */
  uploadFile(token: string, file: File, options: UploadOptions = {}): Promise<{ file: DeskFile }> {
    const query = new URLSearchParams({ name: file.name });
    if (options.roomId) query.set('roomId', options.roomId);
    if (options.folderId) query.set('folderId', options.folderId);
    if (options.kind) query.set('kind', options.kind);
    if (options.width && options.height) {
      query.set('w', String(Math.round(options.width)));
      query.set('h', String(Math.round(options.height)));
    }
    if (options.durationMs !== undefined && Number.isFinite(options.durationMs)) query.set('durationMs', String(Math.round(options.durationMs)));
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', serverUrl(`/api/files?${query.toString()}`));
      xhr.setRequestHeader('authorization', `Bearer ${token}`);
      xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) options.onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => {
        let payload: { file?: DeskFile; error?: { message?: string; code?: string } } | null = null;
        try {
          payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          // resposta sem JSON: fica a mensagem genérica
        }
        if (xhr.status >= 200 && xhr.status < 300 && payload?.file) resolve({ file: payload.file });
        else reject(new ApiError(xhr.status, payload?.error?.message ?? say('api.uploadFailed', { status: xhr.status }), payload?.error?.code));
      };
      xhr.onerror = () => reject(new ApiError(0, say('api.unreachable')));
      xhr.onabort = () => reject(new DOMException(say('api.uploadCanceled'), 'AbortError'));
      if (options.signal?.aborted) {
        reject(new DOMException(say('api.uploadCanceled'), 'AbortError'));
        return;
      }
      options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(file);
    });
  },

  async setAvatar(token: string, image: Blob): Promise<{ user: User }> {
    const response = await fetch(serverUrl('/api/me/avatar'), { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': image.type }, body: image });
    const payload = (await response.json().catch(() => null)) as { user?: User; error?: { message?: string } } | null;
    if (!response.ok || !payload?.user) throw new ApiError(response.status, payload?.error?.message ?? say('api.status', { status: response.status }));
    return { user: payload.user };
  },

  removeAvatar: (token: string) => request<{ user: User }>('/api/me/avatar', { method: 'DELETE', token }),

  /** Foto da pessoa nesta mesa (a própria, ou a de outro se for mestre). */
  async setRoomAvatar(token: string, roomId: string, userId: string, image: Blob): Promise<{ avatarVersion: number }> {
    const response = await fetch(serverUrl(`/api/rooms/${roomId}/avatars/${userId}`), { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': image.type }, body: image });
    const payload = (await response.json().catch(() => null)) as { avatarVersion?: number; error?: { message?: string } } | null;
    if (!response.ok || typeof payload?.avatarVersion !== 'number') throw new ApiError(response.status, payload?.error?.message ?? say('api.status', { status: response.status }));
    return { avatarVersion: payload.avatarVersion };
  },

  removeRoomAvatar: (token: string, roomId: string, userId: string) => request<{ avatarVersion: number }>(`/api/rooms/${roomId}/avatars/${userId}`, { method: 'DELETE', token }),

  deleteRoom: (token: string, roomId: string) =>
    request<{ ok: boolean }>(`/api/rooms/${roomId}`, { method: 'DELETE', token }),

  /** O catálogo de plugins oficiais (docs/spec/web-plugins.md, "Plugins oficiais"): o que o mestre instala pela aba Oficiais. */
  officialPlugins: (token: string) => request<{ plugins: OfficialPlugin[] }>('/api/plugins/official', { token }),

  /* ---------------------------------------------------------------- administração (docs/spec/admin.md) */

  adminUsers: (token: string, query: { q: string; filter: AdminUserFilter; plan: AdminPlanFilter; sort: AdminUserSort; offset: number }) =>
    request<{ users: AdminUserRow[]; total: number }>(`/api/admin/users?${new URLSearchParams({ ...query, offset: String(query.offset) }).toString()}`, { token }),

  adminUser: (token: string, userId: string) => request<{ user: AdminUserDetail }>(`/api/admin/users/${userId}`, { token }),

  adminSuspend: (token: string, userId: string, reason: string) =>
    request<{ ok: boolean }>(`/api/admin/users/${userId}/suspension`, { method: 'POST', token, body: { reason } }),

  adminUnsuspend: (token: string, userId: string) => request<{ ok: boolean }>(`/api/admin/users/${userId}/suspension`, { method: 'DELETE', token }),

  adminSetPassword: (token: string, userId: string, password: string) =>
    request<{ ok: boolean }>(`/api/admin/users/${userId}/password`, { method: 'POST', token, body: { password } }),

  /** `displayName`: o nome digitado, que o servidor confere com o da conta. */
  adminDelete: (token: string, userId: string, displayName: string) =>
    request<{ ok: boolean }>(`/api/admin/users/${userId}`, { method: 'DELETE', token, body: { displayName } }),

  adminSetRole: (token: string, userId: string, admin: boolean) =>
    request<{ ok: boolean }>(`/api/admin/users/${userId}/admin`, { method: admin ? 'PUT' : 'DELETE', token }),

  /** Muda o plano da conta (docs/spec/planos.md): o plano, o prazo (null sem prazo; sempre null no Grátis) e a nota interna. */
  adminSetPlan: (token: string, userId: string, body: { plan: PlanId; expiresAt: number | null; note: string }) =>
    request<{ ok: boolean; plan: PlanView }>(`/api/admin/users/${userId}/plan`, { method: 'PUT', token, body }),

  /** As mesas do servidor, de 50 em 50, com a busca (nome da mesa ou do dono) e a ordem. */
  adminRooms: (token: string, query: { q: string; sort: AdminRoomSort; offset: number }) =>
    request<{ rooms: AdminRoomRow[]; total: number }>(`/api/admin/rooms?${new URLSearchParams({ ...query, offset: String(query.offset) }).toString()}`, { token }),

  /** `name`: o nome digitado, que o servidor confere com o da mesa. */
  adminDeleteRoom: (token: string, roomId: string, name: string) => request<{ ok: boolean }>(`/api/admin/rooms/${roomId}`, { method: 'DELETE', token, body: { name } }),

  /** A configuração do servidor, em leitura. */
  adminServer: (token: string) => request<AdminServerInfo>('/api/admin/server', { token }),
  /** A seção José da administração (docs/spec/desk-tutor.md): os limites e as últimas 24 horas. */
  adminDeskTutor: (token: string) => request<AdminDeskTutorInfo>('/api/admin/desk-tutor', { token }),
  saveAdminDeskTutor: (token: string, change: DeskTutorChange) => request<AdminDeskTutorInfo>('/api/admin/desk-tutor', { method: 'PUT', token, body: change }),

  /** Os números da Visão geral da administração. */
  adminOverview: (token: string) => request<AdminOverview>('/api/admin/overview', { token }),

  /** Os planos na administração (docs/spec/planos.md, "Editar os planos"): o que vale, o padrão e as contas. */
  adminPlans: (token: string) => request<{ plans: AdminPlanRow[]; ceilings: PlanCeilings; canEdit: boolean }>('/api/admin/plans', { token }),

  /** Quantas contas do plano passariam do espaço dado. */
  adminPlanImpact: (token: string, planId: PlanId, storageBytes: number) =>
    request<{ accounts: number; over: number }>(`/api/admin/plans/${planId}/impact?${new URLSearchParams({ storageBytes: String(storageBytes) }).toString()}`, { token }),

  adminSavePlanConfig: (token: string, planId: PlanId, config: PlanConfig) => request<{ ok: boolean }>(`/api/admin/plans/${planId}`, { method: 'PUT', token, body: config }),

  /** Volta o plano ao padrão do código. */
  adminResetPlanConfig: (token: string, planId: PlanId) => request<{ ok: boolean }>(`/api/admin/plans/${planId}`, { method: 'DELETE', token }),

  /** Os plugins oficiais, também os ocultos; `canEdit`: a conta está na configuração do servidor. */
  adminPlugins: (token: string) => request<{ plugins: AdminOfficialPlugin[]; canEdit: boolean; knownSystems: KnownSystem[] }>('/api/admin/plugins', { token }),

  /**
   * Publica um pacote (o .zip já normalizado pelo navegador; um .html vira .zip lá): um oficial novo,
   * ou a versão nova de um (`officialId`, ou o mesmo `id` de manifesto). `from`: a versão de antes.
   */
  async adminPublishPlugin(token: string, zip: Uint8Array, options: { officialId?: string; systems?: readonly string[]; hidden?: boolean } = {}): Promise<{ plugin: AdminOfficialPlugin; from: string | null }> {
    const query = new URLSearchParams();
    if (options.officialId) query.set('officialId', options.officialId);
    if (options.systems) query.set('systems', options.systems.join(SYSTEMS_SEPARATOR));
    if (options.hidden !== undefined) query.set('hidden', options.hidden ? '1' : '0');
    let response: Response;
    try {
      response = await fetch(serverUrl(`/api/admin/plugins?${query.toString()}`), {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/zip' },
        body: new Blob([zip as BlobPart], { type: 'application/zip' }),
      });
    } catch {
      throw new ApiError(0, say('api.unreachable'));
    }
    const payload = (await response.json().catch(() => null)) as { plugin?: AdminOfficialPlugin; from?: string | null; error?: { message?: string; code?: string } } | null;
    if (!response.ok || !payload?.plugin) throw new ApiError(response.status, payload?.error?.message ?? say('api.status', { status: response.status }), payload?.error?.code);
    return { plugin: payload.plugin, from: payload.from ?? null };
  },

  adminPatchPlugin: (token: string, id: string, patch: { systems?: string[]; hidden?: boolean }) =>
    request<{ plugin: AdminOfficialPlugin }>(`/api/admin/plugins/${id}`, { method: 'PATCH', token, body: patch }),

  adminDeletePlugin: (token: string, id: string) => request<{ ok: boolean }>(`/api/admin/plugins/${id}`, { method: 'DELETE', token }),

  adminLog: (token: string, options: { before?: number; userId?: string } = {}) =>
    request<{ entries: AdminLogEntry[] }>(
      `/api/admin/log?${new URLSearchParams({ ...(options.before ? { before: String(options.before) } : {}), ...(options.userId ? { userId: options.userId } : {}) }).toString()}`,
      { token },
    ),
};
